import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import theme from './DocumentSelectList.module.scss';
import { BaseDocument, BuyerResidentialUnitDocument } from '@/types';
import { ExpandableCard, CardSection, ExpandableCardHandle, CardRow } from '@/components/Cards';
import { DateDisplay } from '@/components/localization';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEyeSlash, faLock } from '@fortawesome/pro-light-svg-icons';
import { getOpenDocumentUrl } from '@/services/documents';
import { useDocumentType, useIsSeller } from '@/utils/hooks';
import SensitiveInfoTooltip from '@/components/SensitiveInfoTooltip';
import { Radio } from '@/components/forms/Radio';
import { DocumentSelectionMode, DocumentSelectionValue } from '.';
import DocumentSelectListItemDetails from './DocumentSelectListItemDetails';
import { FileIcon } from '@ygtechnologies/bygr-frontend-component-library';

export interface DocumentListItemProps {
    document: BaseDocument | BuyerResidentialUnitDocument;
    onSelectionChange(id: DocumentSelectionValue): void;
    selectMode: DocumentSelectionMode;
    selectedValue: DocumentSelectionValue;
}

const DocumentSelectListItem: React.FC<DocumentListItemProps> = ({
    document,
    selectMode,
    onSelectionChange,
    selectedValue,
}) => {
    const [expanded, setExpanded] = useState(false);
    const isSeller = useIsSeller();

    const documentType = useDocumentType(document.documentTypeId);

    const sellerDocument = document as BaseDocument;
    const onClick = selectMode === 'document' ? () => onSelectionChange(document.documentId) : () => setExpanded(!expanded);

    return (
        <ExpandableCard
            onClick={onClick}
            expanded={expanded}
            className={theme.documentCard}
            type="outside"
            wrap
            expandableContent={
                <DocumentSelectListItemDetails
                    key={document.lastVersionId}
                    document={document}
                    selectedValue={selectedValue}
                    selectMode={selectMode}
                    onSelectionChange={onSelectionChange}
                />
            }>
            <CardRow>
                {selectMode === 'document' && (
                    <Radio
                        checked={document.documentId === selectedValue}
                        onChange={() => onSelectionChange(sellerDocument.documentId)}
                        labelClassName={theme.selectDocumentRadio}
                    />
                )}

                <div className={theme.info}>
                    <a
                        onClick={(e) => {
                            e.preventDefault();
                            if (selectMode === 'version') {
                                setExpanded(!expanded);
                            }
                        }}
                        href={getOpenDocumentUrl(document.lastVersionId)}
                        className={theme.documentLabel}
                        target="_blank"
                        rel="noreferrer noopener">
                        <div className={theme.fileIconWrapper}>
                            <FileIcon value={document.lastVersionFileMetadata} size="3x" />
                        </div>

                        <div className={theme.type}>
                            <div>{documentType && documentType.name}</div>
                            <small>{document.name}</small>

                            {isSeller && (
                                <div className={theme.visibility}>
                                    {sellerDocument.isSensitive && (
                                        <SensitiveInfoTooltip profileId={sellerDocument.ownerProfileId}>
                                            <small>
                                                <span className={theme.smallInfo}>
                                                    <FontAwesomeIcon icon={faLock} />
                                                    <Trans>Sensitive</Trans>
                                                </span>
                                            </small>
                                        </SensitiveInfoTooltip>
                                    )}

                                    {sellerDocument.hideFromBuyer && (
                                        <small>
                                            <FontAwesomeIcon icon={faEyeSlash} />
                                            <Trans>Hidden from buyer</Trans>
                                        </small>
                                    )}
                                </div>
                            )}
                        </div>
                    </a>
                </div>

                <CardSection caption={<Trans>Modified</Trans>} right width="250px" shrink>
                    <div>
                        <DateDisplay value={document.lastVersionDate} />
                        {isSeller && sellerDocument.uploadedByName && (
                            <Trans>
                                <small>&nbsp;by {sellerDocument.uploadedByName}</small>
                            </Trans>
                        )}
                    </div>
                </CardSection>

                <ExpandableCardHandle expanded={expanded} onExpandToggle={() => setExpanded(!expanded)} />
            </CardRow>
        </ExpandableCard>
    );
};

export default DocumentSelectListItem;